// NewKBRecordCallModal-callProcessing.js
import api from '../../../../services/api'; // Ensure this path is correct

/**
 * Prepares the call data object based on call status and inputs.
 * Does not handle 'yes' outcome's follow-up modal logic; that's for the main component.
 * @param {string} callStatus - The status of the call ('yes', 'no', 'reschedule').
 * @param {string} callNotes - Notes from the call.
 * @param {string} rescheduleTimeInput - The reschedule time if status is 'reschedule'.
 * @param {object} lead - The lead object.
 * @param {function} calculateNextFollowUpDetailsForNoAnswer - Utility function.
 * @param {function} toast - The Chakra UI toast function.
 * @returns {object|null} Prepared call data object or null if validation fails.
 */
export const prepareBaseCallData = (
  callStatus,
  callNotes,
  rescheduleTimeInput,
  lead,
  calculateNextFollowUpDetailsForNoAnswer,
  toast
) => {
  if (!callStatus) {
    toast({
      title: 'Call Status Required',
      description: 'Please select a call status.',
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
    return null;
  }

  if (!lead?.leadId && !lead?._id) {
    toast({
      title: 'Lead Not Specified',
      description: 'Please save the lead before recording call logs.',
      status: 'warning',
      duration: 5000,
      isClosable: true,
    });
    return null;
  }

  const now = new Date();
  const leadId = lead.leadId || lead._id;
  let baseCallData = {
    leadId,
    notes: callNotes.trim() || '', // Default notes can be overridden by specific outcomes
    timestamp: now.toISOString(),
  };

  if (callStatus === 'no') {
    const currentCallCount = Number(lead.callCount || 0);
    const { isoNextFollowUp, notesSuffix } = calculateNextFollowUpDetailsForNoAnswer(currentCallCount);
    baseCallData = {
      ...baseCallData,
      outcome: 'not_answered',
      rescheduledTo: isoNextFollowUp,
      notes: callNotes.trim() || notesSuffix, // Use provided notes or the suffix
    };
  } else if (callStatus === 'reschedule') {
    if (!rescheduleTimeInput) {
      toast({
        title: 'Reschedule Time Required',
        description: 'Please select a reschedule time.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return null;
    }
    baseCallData = {
      ...baseCallData,
      outcome: 'rescheduled',
      rescheduledTo: new Date(rescheduleTimeInput).toISOString(),
      notes: callNotes.trim() || 'Call rescheduled by user',
    };
  } else if (callStatus === 'yes') {
    // For 'yes', the main component will handle specific notes and potential reschedule time.
    // This function just sets the outcome.
    baseCallData = {
      ...baseCallData,
      outcome: 'answered',
      notes: callNotes.trim() || 'Call completed successfully', // Default notes for 'yes'
    };
  }
  return baseCallData;
};

/**
 * Persists the call log to the server and handles associated task creation.
 * @param {object} params - Parameters object.
 * @param {object} params.callDataToSave - The call data to be saved.
 * @param {boolean} params.createTask - Flag to create an associated task.
 * @param {object} params.taskData - Data for the task if createTask is true.
 * @param {object} params.lead - The lead object.
 * @param {function} params.toast - The Chakra UI toast function.
 * @param {function} params.fetchCallHistoryCallback - Callback to refresh call history.
 * @param {function} params.resetFormCallback - Callback to reset the main call form.
 * @param {function} params.onSuccessCallback - Callback on successful call log save.
 * @param {function} params.onCloseModalCallback - Callback to close the main modal.
 * @param {function} params.setTaskDataCallback - State setter for task data.
 * @param {function} params.setCreateTaskCallback - State setter for create task flag.
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */
export const persistCallLog = async ({
  callDataToSave,
  createTask,
  taskData,
  lead,
  toast,
  fetchCallHistoryCallback,
  resetFormCallback,
  onSuccessCallback,
  onCloseModalCallback,
  setTaskDataCallback,
  setCreateTaskCallback,
}) => {
  try {
    console.log('[CallProcessing] Saving call log with data:', callDataToSave);
    // Add any pre-save validation for callDataToSave if necessary

    const response = await api.post('/api/call-logs', callDataToSave);

    if (response.status === 201) {
      console.log('[CallProcessing] Call log saved successfully.');
      toast({
        title: 'Call Recorded',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      if (createTask && taskData.title.trim()) {
        try {
          const taskPayload = {
            ...taskData,
            leadId: lead.leadId || lead._id,
            category: 'Follow-up', // Or make this configurable
            status: 'pending',
            createdBy: callDataToSave.createdBy || null, // Assuming createdBy might be on callData
          };
          console.log('[CallProcessing] Creating task with data:', taskPayload);
          const taskResponse = await api.post('/api/tasks', taskPayload);

          if (taskResponse.status === 201) {
            console.log('[CallProcessing] Task created successfully.');
            toast({
              title: 'Task Created',
              description: 'A new task has been created for this lead.',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            if (typeof setTaskDataCallback === 'function') {
                setTaskDataCallback({ title: '', priority: 'medium', dueDate: '', assignedTo: { email: '' } });
            }
            if (typeof setCreateTaskCallback === 'function') {
                setCreateTaskCallback(false);
            }
          }
        } catch (taskError) {
          console.error('[CallProcessing] Error creating task:', taskError);
          toast({
            title: 'Task Creation Failed',
            description: taskError.message || 'Could not create associated task.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          // Continue even if task creation fails, call log is saved.
        }
      }

      if (typeof fetchCallHistoryCallback === 'function') fetchCallHistoryCallback();
      if (typeof resetFormCallback === 'function') resetFormCallback();

      if (typeof onSuccessCallback === 'function') {
        const updatedLead = {
          ...lead,
          nextFollowUpTime: callDataToSave.rescheduledTo || lead.nextFollowUpTime,
          callCount: (Number(lead.callCount || 0) + 1),
        };
        onSuccessCallback(updatedLead, callDataToSave);
      }

      if (typeof onCloseModalCallback === 'function') onCloseModalCallback(); // Close the main modal
      return true; // Indicate success
    } else {
      // Handle non-201 success statuses if your API might return them
      console.error('[CallProcessing] Error recording call: API returned status', response.status);
       toast({
        title: 'Error Recording Call',
        description: `Server responded with status ${response.status}.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  } catch (error) {
    console.error('[CallProcessing] Error recording call:', error);
    toast({
      title: 'Error Recording Call',
      description: error.response?.data?.message || 'Something went wrong while saving the call log.',
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
    return false;
  }
};